import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { WebAddresses } from '../app.config';
/**
 * Shape of a user-permission entry as returned by GET /userpermissions.
 * Mirrors the JWT token payload structure.
 */
// AFTER
export interface PermissionRef {
  entry_id: number;
  key: string;
}

export interface UserPermissionEntry {
  usr_id: number;
  usr_name: string;
  usr_mail: string;
  org_id: number;
  org_name: string;
  permissions: PermissionRef[];
}

/**
 * Shape of the API envelope used across all endpoints.
 */
export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

/**
 * Service for the user permission administration endpoints.
 *
 * Wraps:
 *   GET    /userpermissions          - findForOrganization
 *   POST   /userpermissions          - create a single permission assignment
 *   DELETE /userpermissions/:id      - delete a single permission assignment by entry ID
 *
 * All requests attach the stored JWT as a Bearer token.
 *
 * @author julian.f.verhey
 */

@Injectable({
  providedIn: 'root',
})
export class UserpermissionService {
  private readonly base = `${WebAddresses.TicketAuthorisation}/userpermissions`;

  constructor(private http: HttpClient) {}

  /**
   * Build Authorization headers from the stored access token.
   */
  private authHeaders(): { headers: HttpHeaders } {
    const token = localStorage.getItem('accessToken') ?? '';
    return {
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Retrieve all users and their permissions for the admin's org context.
   *
   * @returns Observable wrapping the list of UserPermissionEntry objects
   */
  getForOrganization(): Observable<ApiResponse<UserPermissionEntry[]>> {
    return this.http.get<ApiResponse<UserPermissionEntry[]>>(
      `${this.base}/findfororganization`,
      this.authHeaders(),
    );
  }

  /**
   * Assign a permission to a user within the admin's org context.
   *
   * @param upm_user_id       - ID of the target user
   * @param upm_permission_id - ID of the master permission to assign
   * @returns Observable wrapping the created permission entry
   */
  createPermission(
    userId: number,
    permissionId: number,
    orgId?: number,
  ): Observable<any> {
    return this.http
      .post<ApiResponse<any>>(
        `${this.base}/create`,
        {
          upm_user_id: userId,
          upm_permission_id: permissionId,
          ...(orgId !== undefined && { org_id: orgId }), // only include if provided
        },
        this.authHeaders(),
      )
      .pipe(
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  /**
   * Remove a user permission entry by its primary key.
   *
   * @param id - Primary key of the userpermission row to delete
   * @returns Observable wrapping the deleted entry
   */
  deletePermission(id: number): Observable<ApiResponse<any>> {
    console.log(id);
    return this.http.delete<ApiResponse<any>>(
      `${this.base}/delete/${id}`,
      this.authHeaders(),
    );
  }

  notifyUser(userId: number): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.base}/notify/${userId}`,
      {},
      this.authHeaders(),
    );
  }

  /**
   * Delete a user and all their permission entries within the admin's org context.
   *
   * @param userId - ID of the user to delete
   * @returns Observable wrapping the deleted user entry
   */
  deleteUserFromOrganization(userId: number): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${this.base}/removeuserfromorganization/${userId}`,
      this.authHeaders(),
    );
  }
}
